import type { CityConfig } from "@/pages/city-template/types";

export const montpelierConfig: CityConfig = {
  city: "Montpelier",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "montpelier",
  route: "/cities/montpelier",
  title: "Emergency Passport Montpelier, VT — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Montpelier, VT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Montpelier, Barre, Waterbury, and all of Central Vermont.",
  h1: "Expedited Passport Services in Montpelier, VT",
  heroSubtitle:
    "Montpelier travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No trip to the Vermont Passport Agency required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Beautiful%20golden%20hour%20view%20of%20Montpelier%20Vermont%20state%20capital%20featuring%20historic%20Vermont%20State%20House%20with%20golden%20dome%2C%20charming%20downtown%20Main%20Street%20with%20historic%20buildings%2C%20tree-lined%20streets%20with%20autumn%20foliage%2C%20warm%20amber%20light%2C%20peaceful%20New%20England%20capital%20cityscape%2C%20rolling%20green%20hills%20in%20background%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=vt-mont-hero-001&orientation=landscape",
  heroImageAlt: "Montpelier Vermont State House with golden dome and historic downtown",
  geoPosition: "44.2601; -72.5754",
  geoPlacename: "Montpelier, Vermont, United States",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Vermont Passport Agency",
    address: "10 Causeway St Suite 247, Boston MA 02222",
    distance: "Approximately 190 miles, 3 hours via I-89",
  },
  nearbyCitiesZips: [
    { name: "Montpelier", zips: "05601, 05602, 05603, 05604, 05609, 05620, 05633" },
    { name: "Barre", zips: "05641" },
    { name: "Waterbury", zips: "05671, 05676, 05677" },
    { name: "Northfield", zips: "05663, 05664" },
    { name: "Randolph", zips: "05060" },
    { name: "Chelsea", zips: "05038" },
    { name: "East Montpelier", zips: "05651" },
    { name: "Middlesex", zips: "05602" },
    { name: "Berlin", zips: "05602" },
    { name: "Moretown", zips: "05660" },
    { name: "Waitsfield", zips: "05673" },
    { name: "Warren", zips: "05674" },
    { name: "Stowe", zips: "05672" },
    { name: "Burlington", zips: "05401, 05402, 05405, 05406" },
    { name: "South Burlington", zips: "05403, 05407" },
    { name: "Essex Junction", zips: "05452, 05453" },
    { name: "Williston", zips: "05495" },
    { name: "Middlebury", zips: "05753" },
    { name: "Rutland", zips: "05701, 05702" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Montpelier travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Central Vermont",
      desc: "Montpelier, Barre, Waterbury, Northfield, and all surrounding Central Vermont communities.",
    },
  ],
  testimonials: [
    {
      name: "Jennifer L.",
      location: "Montpelier, VT",
      rating: 5,
      text: "I needed my passport renewed for a business trip to Toronto in 6 days. Fast Passport Center had it back to me in Montpelier in 4 days. Their Central Vermont service is outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Robert T.",
      location: "Barre, VT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They helped me navigate the process from Barre and I had my passport in 5 days. Great experience.",
      service: "New Passport",
    },
    {
      name: "Amanda R.",
      location: "Waterbury, VT",
      rating: 5,
      text: "My son needed a passport for a school trip to Europe. Fast Passport Center handled the child passport process and had it ready in 4 days. Waterbury families can trust them.",
      service: "Child Passport",
    },
    {
      name: "David M.",
      location: "Northfield, VT",
      rating: 5,
      text: "Lost my passport before a ski trip to Switzerland. Fast Passport Center replaced it in 3 days and saved my vacation. Northfield residents should use them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "mont-1",
      name: "Montpelier Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "100 Main Street",
      city: "Montpelier",
      state: "VT",
      zip: "05601",
      phone: "802-223-5411",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Montpelier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "mont-2",
      name: "Barre Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "100 Main Street",
      city: "Barre",
      state: "VT",
      zip: "05641",
      phone: "802-476-4161",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Barre",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "mont-3",
      name: "Waterbury Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "100 Main Street",
      city: "Waterbury",
      state: "VT",
      zip: "05671",
      phone: "802-244-1823",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Waterbury",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "mont-4",
      name: "Northfield Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "100 Main Street",
      city: "Northfield",
      state: "VT",
      zip: "05663",
      phone: "802-485-2551",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Northfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "mont-5",
      name: "Stowe Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "100 Main Street",
      city: "Stowe",
      state: "VT",
      zip: "05672",
      phone: "802-253-7351",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Stowe",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "mont-6",
      name: "Montpelier City Clerk",
      type: "City Clerk / Passport Services",
      address: "39 Main Street",
      city: "Montpelier",
      state: "VT",
      zip: "05601",
      phone: "802-223-9500",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Montpelier City Hall",
      website: "https://www.montpelier-vt.org/",
      websiteLabel: "City of Montpelier",
      notes: "Provides passport application acceptance services. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Montpelier, VT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the Vermont Passport Agency if I live in Montpelier?",
      a: "No — if you use Fast Passport Center, you don't need to visit the Vermont Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Montpelier?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Montpelier, Barre, and Waterbury. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Montpelier?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Montpelier?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Montpelier?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Montpelier. We serve Montpelier and all of Central Vermont through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Montpelier for international travel?",
      a: "Burlington International Airport (BTV) is the closest at ~40 miles. Boston Logan (BOS) is ~180 miles, Albany (ALB) is ~150 miles, and Manchester (MHT) is ~130 miles. Most Montpelier residents use BTV for domestic travel and connect through major hubs for international flights.",
    },
    {
      q: "How much does expedited passport service cost in Montpelier?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Montpelier?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Montpelier?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Montpelier?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Montpelier have a local passport agency?",
      a: "No, Montpelier does not have a regional passport agency. The nearest regional agency is the Vermont Passport Agency at 10 Causeway St Suite 247, approximately 185 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Montpelier", address: "various CVS locations in Montpelier", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Montpelier", address: "various Walgreens locations in Montpelier", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Montpelier", address: "various UPS Store locations in Montpelier", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Montpelier", address: "various FedEx Office locations in Montpelier", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Barre", address: "various Walmart locations in Barre", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "VT DMV — Montpelier",
      address: "118 State Street, Montpelier, VT 05602",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Main Vermont DMV office. Accepts walk-ins but appointments are strongly recommended.",
      featured: true,
    },
    {
      name: "VT DMV — Burlington",
      address: "108 Cherry Street, Burlington, VT 05401",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BTV",
      name: "Burlington International Airport",
      distance: "~40 miles from Montpelier",
      address: "1200 Airport Dr, South Burlington, VT 05403",
      note: "Vermont's primary airport. Offers domestic flights and seasonal international connections to Canada. Most Montpelier residents use BTV for domestic travel.",
      flightUrl: "https://www.btv.aero/",
      flightLabel: "Burlington International",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~180 miles from Montpelier",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with flights to virtually every global destination. Common for Montpelier residents on international trips.",
      flightUrl: "https://www.massport.com/logan-airport/",
      flightLabel: "Logan International",
      featured: false,
    },
    {
      code: "MHT",
      name: "Manchester-Boston Regional Airport",
      distance: "~130 miles from Montpelier",
      address: "1 Airport Rd, Manchester, NH 03103",
      note: "Alternative for Montpelier travelers with Southwest and other domestic carriers.",
      flightUrl: "https://www.flymanchester.com/",
      flightLabel: "Manchester Regional",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Montpelier travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Montpelier residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Montpelier residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Montpelier residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Montpelier, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Montpelier residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Montpelier travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Montpelier residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Montpelier residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Montpelier residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Montpelier." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Montpelier." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Montpelier travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Montpelier travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Montpelier team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Montpelier address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 190 miles to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Weekend Availability", agency: "No — business hours only", fpc: "Yes — we work weekends for urgent cases" },
    { feature: "Photo Review", agency: "No — your problem if rejected", fpc: "Yes — pre-submission verification" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Burlington", route: "/cities/burlington" },
    { name: "Montpelier", route: "/montpelier" },
    { name: "Rutland", route: "/rutland" },
    { name: "Stowe", route: "/stowe" },
    { name: "Brattleboro", route: "/brattleboro" },
    { name: "Bennington", route: "/bennington" },
    { name: "Middlebury", route: "/middlebury" },
    { name: "Woodstock", route: "/woodstock-vt" },
    { name: "Springfield", route: "/springfield-vt" },
    { name: "White River Junction", route: "/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Montpelier and all of Central Vermont as a remote courier service. We do not have a physical office in Montpelier, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "50 South Main Street, St. Albans, VT 05478",
  fpcOfficeDistance: "Remote courier service — Montpelier, VT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Montpelier neighborhoods and surrounding Central Vermont communities. From the State House district to the Hill Section, from downtown to the outskirts — we serve every Montpelier community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Montpelier and all surrounding Central Vermont communities. Whether you're in downtown Montpelier or a nearby town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Montpelier customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Montpelier travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Montpelier and Central Vermont travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Montpelier, VT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/vermont",
  statePageName: "Vermont Passport Services",
  dmvWebsite: "https://dmv.vermont.gov",
  dmvLabel: "Vermont DMV — Official Website",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth & Death Certificates", desc: "Request certified Vermont birth and death certificates from the Vermont Department of Health Vital Records in Burlington — online, by mail, or in person.", url: "https://www.healthvermont.gov/health-statistics-vital-records/vital-records-population-data" },
    { icon: "ri-hearts-line", label: "Marriage Records", desc: "Obtain certified marriage certificates from the Montpelier City Clerk at 39 Main Street. Required for name change passport applications.", url: "https://www.montpelier-vt.org/" },
    { icon: "ri-file-copy-line", label: "Divorce Decrees", desc: "Request certified divorce decrees from the Washington County Family Court. Required for name change passport corrections.", url: "https://www.vermontjudiciary.org/courts/family-division" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Vermont, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};

export default montpelierConfig;